/**
 * PortalPanel — map travel + the daily dungeon.
 *
 * MAPAS tab: cards with monster previews and a "Viajar" button that switches
 * the active map. MASMORRAS tab: one attempt/day at a tougher solo boss per
 * tier (unlocked from Tier 3 by hero level) with a guaranteed rarity+ reward;
 * optionally spend a Protection Amulet for -30% incoming damage on the run.
 *
 * @module ui/panels/PortalPanel
 */

import { BasePanel } from './BasePanel.js';
import { bus } from '../../core/events.js';
import { canEnterDungeon, enterDungeon } from '../../core/dungeon.js';
import { dungeonRemainingMs, formatLockTime } from '../../core/inventory.js';
import { bossKillThreshold } from '../../core/combat.js';
import { confirmModal } from '../helpers/confirmModal.js';

export class PortalPanel extends BasePanel {
  constructor(opts) {
    super({
      name: 'portal',
      title: 'PORTAL',
      tabs: [
        { id: 'maps', label: 'Mapas' },
        { id: 'dungeons', label: 'Masmorras' },
      ],
      state: opts.state,
      data: opts.data,
      hooks: opts.hooks,
    });
    this._useAmuletByDungeon = {};
  }

  render() {
    if (this._activeTab === 'dungeons') this._renderDungeons();
    else this._renderMaps();
  }

  // ---- Tab: Mapas ----

  _renderMaps() {
    const maps = this.data.maps ?? {};
    const activeId = this.state.activeMapId;
    const heroLevel = this.state.party[0]?.level ?? 1;
    const entries = Object.entries(maps);

    if (!entries.length) {
      this.body.innerHTML = '<p class="pd-empty">Nenhum portal disponível.</p>';
      return;
    }

    let html = '<div style="display:flex;flex-direction:column;gap:10px">';

    for (const [mapId, map] of entries) {
      const isActive = mapId === activeId;
      const tierLabel = map.tier ? `Tier ${map.tier}` : '';
      const minLevel = map.levelRange?.[0] ?? 1;
      const levelRange = map.levelRange ? `Nv.${map.levelRange[0]}–${map.levelRange[1]}` : '';
      const tooLow = !isActive && heroLevel < minLevel;

      // Monster preview
      const monsterNames = (map.monsters ?? [])
        .map((m) => m.label)
        .slice(0, 3)
        .join(', ');
      const bossName = map.boss?.label ?? '???';

      html += `
        <div style="
          background:var(--pd-surface);
          border:2px solid ${isActive ? 'var(--pd-gold)' : tooLow ? 'var(--pd-border)' : '#6aad6a'};
          border-radius:10px;
          padding:12px;
          opacity:${tooLow ? '0.65' : '1'};
          transition:all var(--pd-transition);
          ${isActive ? 'box-shadow:0 0 20px rgba(232,180,84,0.12)' : ''}
        ">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
            <div>
              <div style="font-weight:800;color:var(--pd-chrome-text);font-size:1rem">${map.label}</div>
              <div style="font-size:10px;color:var(--pd-muted)">
                ${[tierLabel, levelRange].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button class="pd-btn pd-btn-${isActive ? 'secondary' : 'primary'}"
                    data-action="travel" data-map="${mapId}"
                    style="font-size:11px;padding:4px 12px"
                    ${isActive || tooLow ? 'disabled' : ''}>
              ${isActive ? 'Atual' : tooLow ? `Nv.${minLevel}` : 'Viajar'}
            </button>
          </div>
          ${tooLow ? `<div style="font-size:10px;color:#e8a054;margin-bottom:6px">⚠ Recomendado: Nv.${minLevel}+</div>` : ''}
          <div style="font-size:11px;color:var(--pd-text);margin-bottom:4px">
            🐺 <strong>Monstros:</strong> ${monsterNames}
          </div>
          <div style="font-size:11px;color:var(--pd-rarity-legendary)">
            💀 <strong>Chefe:</strong> ${bossName}
            ${map.boss ? `(a cada ${bossKillThreshold(map)} kills)` : ''}
            ${isActive && map.boss ? `<span style="color:var(--pd-gold)"> · faltam ${Math.max(0, bossKillThreshold(map) - (this.state.killsSinceBoss ?? 0))}</span>` : ''}
          </div>
        </div>`;
    }

    html += '</div>';
    this.body.innerHTML = html;

    this.body.querySelectorAll('[data-action="travel"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mapId = btn.dataset.map;
        if (mapId && mapId !== this.state.activeMapId) {
          bus.emit('ui:changeMap', { mapId });
        }
      });
    });
  }

  // ---- Tab: Masmorras ----

  _renderDungeons() {
    const dungeons = this.data.dungeons ?? {};
    const entries = Object.entries(dungeons);
    if (!entries.length) {
      this.body.innerHTML = '<p class="pd-empty">Nenhuma masmorra disponível.</p>';
      return;
    }

    const cooldownMs = dungeonRemainingMs(this.state);
    const onCooldown = cooldownMs > 0;
    const amuletCount = this.state.consumables.protectionAmulets ?? 0;
    const heroLevels = (this.state.party ?? []).map((h) => h.level);
    const maxHeroLevel = heroLevels.length ? Math.max(...heroLevels) : 1;

    let html = `
      <div style="text-align:center;margin-bottom:10px;font-size:11px;color:var(--pd-muted)">
        1 tentativa por dia · derrote o chefe corrompido para um item raro+ garantido.
        ${onCooldown ? `<div style="margin-top:4px;color:#e8a054">⏳ Próxima tentativa em ${formatLockTime(cooldownMs)}</div>` : ''}
        <div style="margin-top:4px">🛡️ Amuletos de Proteção: <strong style="color:var(--pd-gold)">${amuletCount}</strong> (-30% dano na masmorra)</div>
      </div>
      <div style="display:flex;flex-direction:column;gap:10px">`;

    for (const [dungeonId, dungeon] of entries) {
      const rarityColor = this.data.rarity?.[dungeon.guaranteedRarity]?.color ?? '#ff8000';
      const rarityLabel = this.data.rarity?.[dungeon.guaranteedRarity]?.label ?? dungeon.guaranteedRarity;
      const tooLow = maxHeroLevel < dungeon.minLevel;
      const canEnter = !onCooldown && !tooLow && !this.state.dungeonRun;
      const useAmulet = !!this._useAmuletByDungeon[dungeonId] && amuletCount > 0;

      html += `
        <div style="
          background:var(--pd-surface);
          border:2px solid ${tooLow ? 'var(--pd-border)' : rarityColor};
          border-radius:10px;
          padding:12px;
          opacity:${tooLow ? '0.6' : '1'};
        ">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
            <div>
              <div style="font-weight:800;color:var(--pd-chrome-text);font-size:1rem">${dungeon.label}</div>
              <div style="font-size:10px;color:var(--pd-muted)">Tier ${dungeon.tier} · Recomendado Nv.${dungeon.minLevel}+</div>
            </div>
            <button class="pd-btn pd-btn-primary pd-dungeon-enter" data-dungeon="${dungeonId}"
                    style="font-size:11px;padding:4px 12px" ${canEnter ? '' : 'disabled'}>
              ${tooLow ? `Nv.${dungeon.minLevel}` : onCooldown ? 'Indisponível' : 'Entrar'}
            </button>
          </div>
          <div style="font-size:11px;color:var(--pd-rarity-legendary);margin-bottom:4px">
            💀 <strong>${dungeon.boss.label}</strong>
          </div>
          <div style="font-size:11px;color:${rarityColor};margin-bottom:8px">
            🎁 Recompensa garantida: item <strong>${rarityLabel}+</strong>
          </div>
          <label style="display:flex;align-items:center;gap:6px;font-size:10px;color:var(--pd-muted);${amuletCount > 0 ? '' : 'opacity:0.5'}">
            <input type="checkbox" class="pd-dungeon-amulet" data-dungeon="${dungeonId}" ${useAmulet ? 'checked' : ''} ${amuletCount > 0 ? '' : 'disabled'}>
            Usar Amuleto de Proteção (-30% dano)
          </label>
        </div>`;
    }

    html += '</div>';
    this.body.innerHTML = html;

    this.body.querySelectorAll('.pd-dungeon-amulet').forEach((cb) => {
      cb.addEventListener('change', () => {
        this._useAmuletByDungeon[cb.dataset.dungeon] = cb.checked;
      });
    });

    this.body.querySelectorAll('.pd-dungeon-enter').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const dungeonId = btn.dataset.dungeon;
        const dungeon = this.data.dungeons[dungeonId];
        if (!canEnterDungeon(this.state, dungeon)) return;
        const useAmulet = !!this._useAmuletByDungeon[dungeonId] && (this.state.consumables.protectionAmulets ?? 0) > 0;

        // Entering commits the whole day's attempt (win or lose) — confirm first
        // so a stray click never wastes it. In-game modal: native confirm() is
        // blocked inside the PiP window.
        const amuletNote = useAmulet ? '<br>Você vai gastar <strong>1 Amuleto de Proteção</strong> (-30% dano).' : '';
        const ok = await confirmModal(this.root.ownerDocument, {
          icon: '💀',
          title: `Entrar em "${dungeon.label}"?`,
          message: `Esta é sua <strong>ÚNICA</strong> tentativa hoje — se o herói cair, a recompensa é perdida e só libera de novo em 24h.${amuletNote}`,
          confirmLabel: 'Entrar',
        });
        if (!ok) return;

        if (enterDungeon(this.state, this.data, dungeonId, useAmulet)) {
          this.commit();
          bus.emit('panel:requestClose', { name: this.name });
        }
      });
    });
  }
}
